import TopCheckoutbanner from '@/assets/icons/TopCheckoutbanner'
import { ColorPickerCustom as ColorPicker } from '@/components/shared/ColorPicker/ColorPicker'
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect'
import { Input } from '@/components/shared/Input/Input'
import { Label } from '@/components/shared/Label/Label'
import { PhoneInput } from '@/components/shared/PhoneInput/PhoneInput'
import { Switch } from '@/components/ui/switch'
import { TabsContent } from '@/components/ui/tabs'
import WhatsIcon from '@/assets/icons/WhatsIcon'
import { useEffect, useRef, useState } from 'react'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Notifications from '../../icons/Notifications'
import { CustomizeCheckoutData } from '@/@types/Checkouts/edit-checkout'
import { CheckoutHeaderPreview } from '../CheckoutHeaderPreview'

type UrgencyTabProps = {
  values: CustomizeCheckoutData
  setValue: UseFormSetValue<CustomizeCheckoutData>
  register: UseFormRegister<CustomizeCheckoutData>
  errors: FieldErrors<CustomizeCheckoutData>
  clearErrors: UseFormClearErrors<CustomizeCheckoutData>
  productPhotoPreviewUrl: string | null
  checkoutName?: string
  checkoutPrice?: string
}

function formatTime(timeInSeconds: number) {
  const hours = Math.floor(timeInSeconds / 3600)
  const minutes = Math.floor((timeInSeconds % 3600) / 60)
  const seconds = timeInSeconds % 60

  return { hours, minutes, seconds }
}

export function UrgencyTab({
  values,
  setValue,
  register,
  errors,
  clearErrors,
  productPhotoPreviewUrl,
  checkoutName,
  checkoutPrice,
}: UrgencyTabProps) {
  const { t } = useTranslation(['common', 'checkouts', 'sales'])
  const lastWhatsAppSupportRef = useRef<string | null>(null)
  const lastWhatsAppPrefixRef = useRef<string | null>(null)

  const [remainingTime, setRemainingTime] = useState<number | null>(
    (values?.timer_in_minutes ?? 0) * 60,
  )
  const [isCounting, setIsCounting] = useState(false)

  useEffect(() => {
    setRemainingTime((values?.timer_in_minutes ?? 0) * 60)
  }, [values?.timer_in_minutes])

  useEffect(() => {
    lastWhatsAppSupportRef.current = values?.whatsapp_support ?? null
    lastWhatsAppPrefixRef.current = values?.phone_prefix ?? null
  }, [values?.whatsapp_support, values?.phone_prefix])

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>

    if (isCounting && remainingTime !== null && remainingTime > 0) {
      interval = setInterval(() => {
        setRemainingTime((prevTime) =>
          prevTime !== null ? prevTime - 1 : prevTime,
        )
      }, 1000)
    } else if (remainingTime === 0) {
      setIsCounting(false)
    }

    return () => clearInterval(interval)
  }, [isCounting, remainingTime])

  const safeRemainingTime = remainingTime ?? (values?.timer_in_minutes ?? 0) * 60
  const { hours, minutes, seconds } = formatTime(safeRemainingTime)

  const handleWhatsAppChange = (value: string, prefix: string) => {
    setValue('whatsapp_support', value)
    setValue('phone_prefix', prefix)
    lastWhatsAppSupportRef.current = value
    lastWhatsAppPrefixRef.current = prefix
    clearErrors('whatsapp_support')
  }

  return (
    <TabsContent
      value="urgency"
      className="mt-8 flex-col justify-between gap-12 lg:flex-row xl:flex-col"
    >
      <section className="flex w-full grid-cols-2 flex-col gap-6 md:grid md:gap-12 xl:grid-cols-1 2xl:grid-cols-2">
        <section className="w-full space-y-4 lg:max-w-[402px] xl:max-w-none">
          <div className="flex items-center space-x-2">
            <Switch
              checked={values?.show_urgency}
              onCheckedChange={(value) => {
                setValue('show_urgency', value)
                if (!value) {
                  setValue('timer_in_minutes', null)
                  setValue('urgency_description', undefined)
                  setValue('urgency_background_color', '#a1a1a9')
                  setValue('urgency_text_color', '#ffffff')
                  setRemainingTime(0)
                  clearErrors('timer_in_minutes')
                }
              }}
              id="show-urgency"
              data-cy="switch-show-urgency"
              defaultChecked={values?.show_urgency}
            />
            <Label htmlFor="show-urgency">{t('common:show_countdown')}</Label>
          </div>

          {values?.show_urgency && (
            <section className="mb-6 space-y-4">
              <div className="space-y-2">
                <Label>{t('checkouts:countdown_timer')}</Label>
                <div className="flex gap-4">
                  <div className="flex flex-col">
                    <CustomSelect
                      options={Array.from({ length: 24 }).map((_, index) => ({
                        value: index.toString(),
                        label: String(index).padStart(2, '0'),
                      }))}
                      placeholder="00"
                      value={Math.floor(safeRemainingTime / 3600).toString()}
                      onValueChange={(value) => {
                        const nextHours = parseInt(value, 10)
                        const newTime =
                          nextHours * 3600 + (safeRemainingTime % 3600)
                        setRemainingTime(newTime)
                        setValue('timer_in_minutes', Math.floor(newTime / 60))
                        clearErrors('timer_in_minutes')
                      }}
                      dataCy="select-timer-hours"
                    />
                  </div>

                  <div className="flex flex-col">
                    <CustomSelect
                      options={Array.from({ length: 60 }).map((_, index) => ({
                        value: index.toString(),
                        label: String(index).padStart(2, '0'),
                      }))}
                      placeholder="00"
                      value={Math.floor(
                        (safeRemainingTime % 3600) / 60,
                      ).toString()}
                      onValueChange={(value) => {
                        const nextMinutes = parseInt(value, 10)
                        const newTime =
                          Math.floor(safeRemainingTime / 3600) * 3600 +
                          nextMinutes * 60 +
                          (safeRemainingTime % 60)
                        setRemainingTime(newTime)
                        setValue('timer_in_minutes', Math.floor(newTime / 60))
                        clearErrors('timer_in_minutes')
                      }}
                      dataCy="select-timer-minutes"
                    />
                  </div>
                </div>
                {errors?.timer_in_minutes && (
                  <p className="text-sm text-error">
                    {errors?.timer_in_minutes?.message}
                  </p>
                )}
              </div>
              <div className="flex w-full gap-6">
                <ColorPicker
                  defaultColor={values?.urgency_background_color ?? '#a1a1a9'}
                  onValueChange={(value) =>
                    setValue('urgency_background_color', value)
                  }
                  label={t('common:background_color')}
                />
                <ColorPicker
                  defaultColor={values?.urgency_text_color ?? '#ffffff'}
                  onValueChange={(value) =>
                    setValue('urgency_text_color', value)
                  }
                  label={t('common:text_color')}
                />
              </div>
              <Input
                label={t('common:display_message')}
                placeholder={t('checkouts:encouragement_message')}
                {...register('urgency_description')}
                maxLength={255}
                hasError={Boolean(errors?.urgency_description)}
                helperText={errors?.urgency_description?.message}
                data-cy="input-urgency-message"
                className="mb-3"
              />
            </section>
          )}

          <div className="flex items-center pt-6 space-x-2 border-t border-t-colorBorder my-6">
            <Switch
              checked={values?.show_notification}
              id="show-notification"
              defaultChecked={values?.show_notification}
              onCheckedChange={(value) => {
                setValue('show_notification', value)
              }}
              data-cy="switch-show-notification"
            />
            <Label htmlFor="show-notification">
              {t('common:show_notifications')}
            </Label>
          </div>

          <div className="flex flex-col gap-4 pt-6 border-t border-t-colorBorder my-6">
            <div className="flex items-center space-x-2">
              <Switch
                id="show-whatsapp"
                checked={Boolean(values?.show_whatsapp_support)}
                onCheckedChange={(value) => {
                  setValue('show_whatsapp_support', value)
                  if (!value) {
                    lastWhatsAppSupportRef.current = (values?.whatsapp_support ??
                      null) as string | null
                    lastWhatsAppPrefixRef.current = (values?.phone_prefix ??
                      null) as string | null
                    clearErrors('whatsapp_support')
                    return
                  }

                  setValue(
                    'phone_prefix',
                    lastWhatsAppPrefixRef.current ??
                      values?.phone_prefix ??
                      '55',
                  )
                  setValue(
                    'whatsapp_support',
                    lastWhatsAppSupportRef.current ??
                      values?.whatsapp_support ??
                      '',
                  )
                }}
                data-cy="switch-show-whatsapp"
              />
              <Label htmlFor="show-whatsapp">
                {t('common:show_whatsapp_support')}
              </Label>
            </div>

            {Boolean(values?.show_whatsapp_support) && (
              <PhoneInput
                label={t('supportWhatsAppLabel')}
                onChangePhone={handleWhatsAppChange}
                phone_prefix={values?.phone_prefix ?? '55'}
                value={values?.whatsapp_support ?? ''}
                placeholder={t('supportWhatsAppPlaceholder')}
                helperText={errors?.whatsapp_support?.message as string}
                data-cy="input-whatsapp-support"
              />
            )}
          </div>
        </section>
        <section className="relative hidden min-h-[335px] w-full rounded-2xl bg-[#18181B] lg:block xl:hidden">
          <article className="flex justify-between rounded-t-2xl items-center p-2 bg-[#27272A]">
            <TopCheckoutbanner />
            <p className="text-xs font-medium text-muted">go.mundpay.com</p>
            <p>&nbsp;</p>
          </article>
          <section
            className="flex gap-4 justify-center min-h-[32px] items-center p-2 bg-muted"
            style={{
              backgroundColor: values.urgency_background_color || '#a1a1a9',
            }}
          >
            {values?.show_urgency && (
              <div
                style={{
                  color: values?.urgency_text_color,
                }}
                className="flex text-colorText items-center max-w-[50%] font-semibold text-xs gap-2"
              >
                <span className="bg-[#FFFFFF33] rounded-[4px] flex items-center justify-center w-6 h-6">
                  {String(hours).padStart(2, '0')}
                </span>
                :
                <span className="bg-[#FFFFFF33] rounded-[4px] flex items-center justify-center w-6 h-6">
                  {String(minutes).padStart(2, '0')}
                </span>
                :
                <span className="bg-[#FFFFFF33] rounded-[4px] flex items-center justify-center w-6 h-6">
                  {String(seconds).padStart(2, '0')}
                </span>
              </div>
            )}
            <div
              style={{
                color: values?.urgency_text_color,
              }}
              className="text-[10px]"
            >
              {values?.urgency_description}
            </div>
          </section>
          <div className="flex items-center justify-center mt-4">
            <CheckoutHeaderPreview
              src={productPhotoPreviewUrl}
              name={values?.name || checkoutName}
              price={checkoutPrice}
            />
          </div>
          {values?.show_notification && (
            <div className="flex items-end justify-end p-4 mt-2">
              <Notifications />
            </div>
          )}
          {Boolean(values?.show_whatsapp_support) && (
            <button
              type="button"
              aria-label={t('common:whatsapp')}
              className="absolute bottom-4 right-4 h-10 w-10 rounded-full bg-[#25D366] shadow-lg flex items-center justify-center"
            >
              <WhatsIcon className="h-5 w-5" />
            </button>
          )}
        </section>
      </section>
    </TabsContent>
  )
}
